import r from '../Utils/fetch'

const w: any = window
const endPoint = `${w.API_CORE_URL}`

/**
 * État de la mesure de consommation GCP.
 *
 * ⚠️ La mesure est un cron silencieux. Sans cet écran, plusieurs nuits d'échec
 * ne se lisent que dans les logs — et se voient ensuite comme une baisse de
 * consommation sur les écrans de facturation, pas comme une panne.
 */
export interface IDwUsageStatus {
	rows: number
	/** Projets GCP distincts relevés. */
	gcpProjects: number
	/** Ceux qu'on sait rattacher à un projet Quanti. */
	attachedProjects: number
	/** L'écart : projets supprimés dont le GCP tourne encore, infra, inconnus. */
	unattachedProjects: number

	firstUsageDate?: string
	lastUsageDate?: string
	lastMeasuredAt?: string

	/** ⚠️ Journées SANS AUCUNE mesure : c'est le seul signal qui distingue « rien consommé » de « pas mesuré ». */
	missingDays: string[]
	missingDaysTruncated: boolean

	retentionDays: number
	measureWindowDays: number
	maxWindowDays: number

	/** Aperçu de la dépense sur les 30 dernières journées couvertes. */
	costs?: IDwUsageCosts
	/** Douze mois glissants, du plus ancien au plus récent : la série du graphe. */
	monthly: IDwMonthlyCost[]
}

export interface IDwMonthlyCost {
	/** « 2026-09 ». */
	month: string
	costEur: number
	unattachedEur: number
	/** ⚠️ Un mois à 0 € peut vouloir dire « rien dépensé » OU « rien mesuré ». */
	measuredDays: number
	daysInMonth: number
	/** Mois non révolu, ou dont des journées manquent : ne se compare pas aux autres. */
	partial: boolean
}

/**
 * ⚠️ Ce sont des COÛTS : ce que Quanti paie à Google. Aucun rapport avec ce qui
 * est refacturé aux clients, qui passe par les produits Store et Track.
 */
export interface IDwUsageCosts {
	from: string
	to: string
	totalEur: number
	/** Part que personne ne réclame : projets supprimés, infra, inconnus. */
	unattachedEur: number
	byService: Array<{ service: string; costEur: number }>
	/** ⚠️ Absent quand la couverture ne remonte pas assez loin — une fenêtre
	 *  précédente partielle afficherait une explosion de la dépense là où il n'y
	 *  a qu'un historique plus court. */
	previousEur?: number
}

export interface IDwMeasureResult {
	projects: number
	rows: number
	from: string
	to: string
}

export const getDwUsageStatus = (): Promise<IDwUsageStatus> =>
	r({ url: `${endPoint}/v1/admin/dw-usage/status`, method: 'GET' })

/**
 * Lance une mesure. `days` remonte plus loin pour rattraper un historique.
 *
 * ⚠️ Le scan de l'export de facturation est PAYANT et proportionnel à la
 * fenêtre : une reprise de 400 jours ne se lance pas par curiosité.
 */
export const triggerDwMeasure = (days?: number): Promise<IDwMeasureResult> =>
	r({
		url: `${endPoint}/v1/admin/dw-usage/measure${days ? `?days=${days}` : ''}`,
		method: 'POST',
	})

export const formatEurCost = (eur: number): string =>
	`${eur.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`

/** « 2026-08-22 » → « 22/08/2026 ». Découpage de chaîne : aucun objet Date, aucun fuseau. */
export const formatDwDay = (iso?: string): string => {
	if (!iso) return ''
	const [y, m, d] = iso.split('-')
	return y && m && d ? `${d}/${m}/${y}` : iso
}

/** Détail d'un mois calendaire. Même forme que l'aperçu du statut. */
export const getDwCosts = (month: string): Promise<IDwUsageCosts> =>
	r({ url: `${endPoint}/v1/admin/dw-usage/costs?month=${month}`, method: 'GET' })

/** « 2026-09 » → « sept. 2026 ». */
export const formatMonth = (month: string): string => {
	const [y, m] = month.split('-')
	const d = new Date(Number(y), Number(m) - 1, 1)
	return d.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })
}
